/**
 * Presentation + client-estimate helpers for the Leave module.
 *
 * The working-day estimate here is DELIBERATELY weekends-only. There is
 * no employee-facing holiday endpoint for the form preview (the form
 * therefore shows a clearly-labelled ESTIMATE; the authoritative
 * total_days is whatever the server returns on submit, weekends AND
 * tenant public holidays excluded). We never present the estimate as
 * final.
 *
 * Migration note (2026-06-01, Next.js → Vite): direct 1:1 port from
 * frontend/src/lib/leave-format.ts. No framework deps — pure helpers.
 */

import { formatDate, formatDateRange, formatDateTime } from "@/lib/format";

/** "5 Nov 2026" — the shared formatter (lib/format), tolerant of a
 *  bad/empty value. */
export function fmtDate(iso?: string | null): string {
  return formatDate(iso);
}

/** A from–to span (lib/format formatDateRange): "16 Jul 2026" for one
 *  day — never "16 Jul 2026 – 16 Jul 2026" — and "3–5 Nov 2026" otherwise. */
export function fmtDateRange(from?: string | null, to?: string | null): string {
  return formatDateRange(from, to);
}

/** "5 Nov 2026, 9:30 am IST" — for decision timestamps / notification
 *  times (lib/format). */
export function fmtDateTime(iso?: string | null): string {
  return formatDateTime(iso);
}

/** Compact relative time ("just now", "5m ago", "3h ago", "2d ago"),
 *  falling back to an absolute date past a week. For the bell feed. */
export function fmtRelative(iso?: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const ms = d.getTime();
  if (Number.isNaN(ms)) return iso;
  const diff = Date.now() - ms;
  if (diff < 0) return "just now";
  const min = Math.floor(diff / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return formatDate(d);
}

/**
 * Inclusive weekday count between two YYYY-MM-DD strings, Sat/Sun
 * excluded. Client estimate only. Returns 0 for an invalid / inverted
 * range.
 */
export function estimateWorkingDays(
  start: string,
  end: string,
): number {
  if (!start || !end) return 0;
  const s = new Date(start + "T00:00:00");
  const e = new Date(end + "T00:00:00");
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return 0;
  if (e < s) return 0;
  let count = 0;
  const cur = new Date(s);
  // Hard ceiling so a fat-fingered far-future end can't spin the loop.
  let guard = 0;
  while (cur <= e && guard < 3700) {
    const dow = cur.getDay(); // 0 Sun … 6 Sat
    if (dow !== 0 && dow !== 6) count += 1;
    cur.setDate(cur.getDate() + 1);
    guard += 1;
  }
  return count;
}

/** "First half" / "Second half" for a half-day request (wave 2). */
export function halfDayLabel(h?: string | null): string {
  if (h === "first_half") return "First Half";
  if (h === "second_half") return "Second Half";
  return "";
}

/** Format a leave-day quantity: "1 day" / "2.5 days". */
export function fmtDays(n: number): string {
  const v = Number.isFinite(n) ? n : 0;
  return `${v % 1 === 0 ? v.toFixed(0) : v.toFixed(1)} ${
    v === 1 ? "day" : "days"
  }`;
}

/**
 * Today as YYYY-MM-DD in the *local* timezone — for date-input min/max +
 * defaults. `toISOString()` is UTC, so for IST users early in the day it
 * returns yesterday; build the string from the local calendar fields so a
 * date picker that "defaults to today" actually shows the user's today.
 */
export function todayISO(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * Display name for a leave-year whose START year is `year` under an anchor
 * month: "2026" for January, "FY 2026-27" for April (the Indian financial
 * year), "2026-27" otherwise. Mirrors models.LeaveSettings.LeaveYearLabel —
 * prefer the server's `leave_year.label` when a response carries one; this
 * is for labelling OTHER years (e.g. a year switcher).
 */
export function leaveYearLabel(year: number, startMonth: number): string {
  if (!startMonth || startMonth === 1) return String(year);
  const next = String((year + 1) % 100).padStart(2, "0");
  return startMonth === 4 ? `FY ${year}-${next}` : `${year}-${next}`;
}

/**
 * True once a leave's start date is BEFORE today (local calendar day).
 * Mirrors the server rule (ErrLeaveStarted): an employee may withdraw
 * approved leave up to and including its start day; after that only HR
 * can cancel it on their behalf.
 */
export function leaveStartHasPassed(startDate: string): boolean {
  return startDate < todayISO();
}
